/**
 * useEditorInit Hook
 *
 * Shared init/reinit lifecycle for all editor components.
 * Encapsulates: communication → API fetch → config build → state machine feed.
 *
 * Extensible: any component type that follows the pattern
 *   "receive entityId → fetch EditorInitData → build EditorConfig → drive state machine"
 * can use this hook by providing a `fetchInitData` function and a `buildConfig` mapper.
 *
 * Handles:
 * - First INIT from Angular → full init flow
 * - Subsequent INITs (version navigation / Save As) → reinit flow
 * - Cleanup of in-flight requests on unmount or re-trigger
 */

import { useEffect, useRef } from 'react';
import type { EditorInitData } from '@services/editorApi.service';
import type { EditorConfig } from '@/types/editor.types';
import type { InitPayload } from '@/types/communication.types';
import type { UseEditorCommunicationReturn } from '@hooks/useEditorCommunication';
import type { UseEditorStateMachineReturn } from '@hooks/useEditorStateMachine';
import type { UseAutosaveReturn } from '@hooks/useAutosave';

// =============================================================================
// TYPES
// =============================================================================

export interface EditorInitCallbacks {
  /** Called with fresh init data on every init/reinit — set component-local state here */
  onInitData: (data: EditorInitData, isReinit: boolean) => void;
  /** Called before reinit starts — reset component-local refs/state here */
  onBeforeReinit?: () => void;
  /** Optional: called after config is fed into the state machine */
  onConfigDispatched?: (config: EditorConfig, isReinit: boolean) => void;
}

export interface UseEditorInitOptions {
  /** The communication hook instance */
  communication: UseEditorCommunicationReturn;
  /** The state machine hook instance */
  stateMachine: UseEditorStateMachineReturn;
  /** The autosave hook instance — reset on reinit */
  autosave: UseAutosaveReturn;
  /** Fetch init data for this component type */
  fetchInitData: (entityId: string, initPayload: InitPayload) => Promise<EditorInitData>;
  /** Build EditorConfig from the fetched data */
  buildConfig: (data: EditorInitData) => EditorConfig;
  /** Component callbacks for init/reinit */
  callbacks: EditorInitCallbacks;
}

export interface UseEditorInitReturn {
  /** Current version ID (stable ref) */
  versionIdRef: React.RefObject<string | null>;
  /** Current init data (stable ref) */
  initDataRef: React.RefObject<EditorInitData | null>;
}

// =============================================================================
// HOOK
// =============================================================================

export function useEditorInit({
  communication,
  stateMachine,
  autosave,
  fetchInitData,
  buildConfig,
  callbacks,
}: UseEditorInitOptions): UseEditorInitReturn {
  const versionIdRef = useRef<string | null>(null);
  const initDataRef = useRef<EditorInitData | null>(null);
  const hasInitializedRef = useRef(false);

  useEffect(() => {
    if (!communication.initPayload) return;

    const initPayload = communication.initPayload;
    const { entityId } = initPayload;
    let cancelled = false;
    const isReinit = hasInitializedRef.current;

    // ── Pre-reinit cleanup ─────────────────────────────────────────────────
    if (isReinit) {
      callbacks.onBeforeReinit?.();
      autosave.reset();
    }

    (async () => {
      try {
        console.log('[DEBUG] useEditorInit: Fetching init data for entityId:', { entityId });
        const data = await fetchInitData(entityId, initPayload);
        console.log('[DEBUG] useEditorInit: Received init data:', { versionId: data.version?._id, hasData: !!data.version?.data, hasSfdtData: !!data.version?.sfdtData, dataLength: data.version?.data?.length, sfdtDataLength: data.version?.sfdtData?.length });
        if (cancelled) return;

        // Update stable refs
        versionIdRef.current = data.version._id;
        initDataRef.current = data;
        hasInitializedRef.current = true;

        // Let the component set its own local state from data
        callbacks.onInitData(data, isReinit);

        // Build config and feed into state machine
        const editorConfig = buildConfig(data);
        console.log('[DEBUG] useEditorInit: Built editor config, dispatching to state machine');

        // Use CONFIG_RECEIVED for both init and reinit — the state machine
        // now supports CONFIG_RECEIVED from any operational state.
        stateMachine.onConfigReceived(editorConfig);

        callbacks.onConfigDispatched?.(editorConfig, isReinit);
      } catch (err) {
        if (cancelled) return;
        console.error('[DEBUG] useEditorInit: Init failed:', err);
        communication.sendError('INIT_FAILED', (err as Error).message);
        stateMachine.onError({
          code: 'COMMUNICATION_ERROR',
          message: 'Failed to initialize editor',
          details: err,
          timestamp: Date.now(),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // initSequence changes on every new INIT — this is the re-trigger mechanism
  }, [communication.initPayload, communication.initSequence]);

  return { versionIdRef, initDataRef };
}
